import { useEffect, useMemo, useState } from "react";

const chunkText = (text) => {
  const words = (text || "").replace(/—/g, "-").split(/\s+/).filter(Boolean);
  const out = [];
  let cur = [];
  for (const w of words) {
    cur.push(w);
    if (cur.length >= 4 || /[.!?,]$/.test(w)) {
      out.push(cur.join(" "));
      cur = [];
    }
  }
  if (cur.length) out.push(cur.join(" "));
  return out.length ? out : ["Bold captions keep muted viewers watching"];
};

const OUTLINE = "[text-shadow:2px_2px_0_#000,-2px_2px_0_#000,2px_-2px_0_#000,-2px_-2px_0_#000,0_2px_0_#000,0_-2px_0_#000]";

const POSITION_CLASS = {
  top: "inset-x-4 top-[12%]",
  middle: "inset-x-4 top-1/2 -translate-y-1/2",
  bottom: "inset-x-4 bottom-[16%]",
};

const SIZE_CLASS = {
  small: "text-base sm:text-lg",
  standard: "text-xl sm:text-2xl",
  large: "text-2xl sm:text-3xl",
};

export default function CaptionPreview({ styleKey, position = "bottom", sizeKey = "standard", text }) {
  const chunks = useMemo(() => chunkText(text), [text]);
  const [idx, setIdx] = useState(0);
  const chunk = chunks[idx % chunks.length];
  const dur = Math.min(2400, Math.max(900, chunk.length * 75));

  useEffect(() => setIdx(0), [styleKey, text]);
  useEffect(() => {
    const t = setTimeout(() => setIdx((i) => (i + 1) % chunks.length), dur);
    return () => clearTimeout(t);
  }, [idx, chunks.length, dur, styleKey]);

  const words = chunk.split(" ");
  const total = words.reduce((a, w) => a + w.length, 0) || 1;
  let acc = 0;

  return (
    <div
      className="relative mt-3 aspect-video w-full overflow-hidden rounded-md border border-white/10 bg-gradient-to-br from-[#1a2036] via-[#0d1020] to-[#05060c]"
      data-testid="caption-preview"
    >
      <span className="absolute top-2 left-2.5 text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">Live preview</span>
      <div className={`absolute text-center ${POSITION_CLASS[position] || POSITION_CLASS.bottom}`}>
        <p key={`${styleKey}-${sizeKey}-${idx}`} className={`font-black uppercase leading-tight ${SIZE_CLASS[sizeKey] || SIZE_CLASS.standard} ${OUTLINE} ${styleKey === "yellow" ? "text-yellow-300" : "text-white"}`}>
          {styleKey === "karaoke"
            ? words.map((w, i) => {
                const delay = (acc / total) * dur;
                acc += w.length;
                return (
                  <span key={i} style={{ animation: `cap-karaoke ${Math.max(80, (w.length / total) * dur)}ms linear ${delay.toFixed(0)}ms forwards` }}>
                    {w}
                    {i < words.length - 1 ? " " : ""}
                  </span>
                );
              })
            : chunk}
        </p>
      </div>
    </div>
  );
}
